import { Box, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";

/**
 * PageLayout Component - Premium Page Container
 * 
 * Provides consistent premium styling across all pages with:
 * - Animated header with gradient text
 * - Optional icon and subtitle
 * - Decorative gradient line
 * - Smooth entrance animations
 * - Full dark mode support
 */
export default function PageLayout(props) {
  const {
    title,
    subtitle,
    icon: TitleIcon,
    actions,
    children,
    maxWidth = "84vw",
    hideHeader = false,
  } = props;

  const theme = useTheme();
  const { palette = {} } = theme;
  const { primary = {}, success = {}, text = {}, divider, mode } = palette;
  const isDark = mode === "dark";

  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";
  const textPrimary = text?.primary || "#1e293b";
  const textSecondary = text?.secondary || "#64748b";
  const dividerColor = divider || "#e2e8f0";

  return (
    <Box
      sx={{
        padding: { xs: "12px", sm: "14px", md: "16px" },
        minHeight: "100%",
        animation: "pageEnter 0.4s ease-out",
        "@keyframes pageEnter": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      <Grid
        container
        sx={{
          margin: "0 auto",
          width: maxWidth,
        }}
      >
        {/* Header Section */}
        {!hideHeader && (
        <Grid size={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
              gap: "12px",
              marginBottom: "8px",
              paddingBottom: "10px",
              borderBottom: `1px solid ${dividerColor}`,
              animation: "headerSlide 0.5s ease-out",
              "@keyframes headerSlide": {
                from: { opacity: 0, transform: "translateY(-8px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            {/* Title Section */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {/* Icon */}
              {TitleIcon && (
                <Box
                  sx={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    background: isDark
                      ? `linear-gradient(135deg, ${greenColor}25 0%, ${greenColor}10 100%)`
                      : `linear-gradient(135deg, ${greenColor}18 0%, ${navyColor}10 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `1px solid ${isDark ? `${greenColor}40` : `${greenColor}25`}`,
                    boxShadow: isDark 
                      ? `0 2px 8px ${greenColor}20`
                      : `0 2px 8px ${greenColor}15`,
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      transform: "scale(1.05)",
                      boxShadow: `0 4px 12px ${greenColor}${isDark ? '35' : '25'}`,
                    },
                  }}
                >
                  <TitleIcon sx={{ fontSize: "1.25rem", color: greenColor }} />
                </Box>
              )}

              <Box>
                {/* Page Title */}
                <Typography
                  component="h1"
                  variant="h4"
                  sx={{
                    color: isDark ? greenColor : navyColor,
                    fontWeight: 700,
                    fontSize: { xs: "1.25rem", sm: "1.375rem" },
                    fontFamily: '"Lato", sans-serif',
                    letterSpacing: "-0.02em",
                    lineHeight: 1.25,
                  }}
                >
                  {title}
                </Typography>

                {/* Subtitle */}
                {subtitle && (
                  <Typography
                    sx={{
                      color: textSecondary,
                      fontSize: "0.8125rem",
                      marginTop: "2px",
                      lineHeight: 1.4,
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Actions */}
            {actions && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexShrink: 0,
                  animation: "actionsSlide 0.5s ease-out 0.1s both",
                  "@keyframes actionsSlide": {
                    from: { opacity: 0, transform: "translateX(8px)" },
                    to: { opacity: 1, transform: "translateX(0)" },
                  },
                }}
              >
                {actions}
              </Box>
            )}
          </Box>

          {/* Decorative gradient line */}
          <Box
            sx={{
              height: "2px",
              marginTop: "-10px",
              marginBottom: "12px",
              borderRadius: "1px",
              background: isDark
                ? `linear-gradient(90deg, ${greenColor} 0%, ${greenColor}50 50%, transparent 100%)`
                : `linear-gradient(90deg, ${greenColor} 0%, ${navyColor}30 50%, transparent 100%)`,
              opacity: isDark ? 0.7 : 0.5,
            }}
          />
        </Grid>
        )}

        {/* Content */}
        <Grid
          size={12}
          sx={{
            animation: "contentFade 0.5s ease-out 0.15s both",
            "@keyframes contentFade": {
              from: { opacity: 0, transform: "translateY(8px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {children}
        </Grid>
      </Grid>
    </Box>
  );
}
